import PropTypes from 'prop-types'
import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faEdit, faRedo, faExclamationCircle, faFilter, faTimes } from "@fortawesome/free-solid-svg-icons";
import useTableFilters from "helpers/useTableFilters";
import { useSelector } from 'react-redux';
import FilterSelect from 'components/shared/FilterSelect';
import { analistasToOptions } from "../../../helpers/selects";
import BaseTable from "../../shared/BaseTable";
import CheckboxDropdown from "../../UI/AnalistSelector";
import Icon from "../../UI/Icon";
import { headerOperation, headerDoubleRowOperation, cellOperation, cellNumOperation } from "../../../helpers/tables";
import { API, BASE_URL, FRONT_DF_1, DEFAULT_PAGE_SIZE_SMALL, NO_FILTERED_ID, PROFILES } from "../../../CONST";
import { patch, get } from "../../../helpers/requests";
import { fdate, formatRut, parseTiempoEspera, getUrgencyColor } from "../../../helpers/helpers";
import isUserHasProfile from '../../../helpers/profileValidator';
import { INBOX_VIEW_WRITE } from '../../../helpers/profilePermission';
import CheckModal from "../../shared/CheckModal";
import SolicitudReevaluacionModal from './SolicitudReevaluacionModal';

const FilterInput = React.memo(
  ({ columnId, placeholder, onChange, minLength, clearSignal, initialValue }) => {
    const [value, setValue] = useState(initialValue || '');
    const isFirstRender = useRef(true);

    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      setValue('');
    }, [clearSignal]);

    useEffect(() => {
      setValue(initialValue || '');
    }, [initialValue]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      setValue(newValue);
      onChange(columnId, newValue);
    };

    return (
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        style={{ width: '100%' }}
      />
    );
  }
);

FilterInput.propTypes = {
  columnId: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  minLength: PropTypes.number,
  clearSignal: PropTypes.number.isRequired,
  initialValue: PropTypes.string,
};
FilterInput.defaultProps = {
  minLength: 1,
  initialValue: '',
};

const FilterSelectWrapper = ({ columnId, options, defaultOption, onChange, clearSignal, initialValue }) => {
  const [localValue, setLocalValue] = useState(initialValue ?? null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setLocalValue(null);
    onChange(columnId, null);
  }, [clearSignal]);

  useEffect(() => {
    setLocalValue(initialValue ?? null);
  }, [initialValue]);

  const handleChange = (value) => {
    setLocalValue(value);
    onChange(columnId, value === NO_FILTERED_ID ? null : value);
  };

  const fakeFilter = localValue !== null ? { id: columnId, value: localValue } : null;

  return (
    <FilterSelect
      filter={fakeFilter}
      defaultOption={defaultOption}
      onFilterChange={handleChange}
      options={options}
    />
  );
};

FilterSelectWrapper.propTypes = {
  columnId: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  defaultOption: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  clearSignal: PropTypes.number.isRequired,
  initialValue: PropTypes.any,
};
FilterSelectWrapper.defaultProps = {
  initialValue: null,
};

const MIN_LENGTHS = {
  'id__icontains': 1,
  'tipo__descripcion__icontains': 3,
  'linea_credito__cliente': 3,
  'poliza': 1,
  'razon': 3,
  'rut': 3,
  'monto_solicitado__icontains': 1,
  'nominado': 3,
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const InboxRiesgoMovimientosTable = ({ setPendientes }) => {
  const user = useSelector((state) => state.login.user)
  const [analistasFilter, setAnalistasFilter] = useState([]);
  const [analistasOptions, setAnalistasOptions] = useState([]);
  const [arrayAnalistas, setArrayAnalistas] = useState([]);
  const [paisesOptions, setPaisesOptions] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [openReevaluacionModal, setOpenReevaluacionModal] = useState(false);
  const [movimientoIDReevaluacion, setMovimientoIDReevaluacion] = useState(0);
  const [userIDReevaluacion, setUserIDReevaluacion] = useState(0)
  const [nombreDeudor, setNombreDeudor] = useState('')
  const [nombreAsegurado, setNombreAsegurado] = useState('')
  const [onFilteredChange, onFetchData, loading, data, getData] = useTableFilters(
    `${BASE_URL}${API.riskInbox}/`,
  );

  // ---- Manual-apply filter state ----
  const [clearSignal, setClearSignal] = useState(0);
  const [activeFilters, setActiveFilters] = useState([]);
  const [filterCount, setFilterCount] = useState(0);
  const filterValuesRef = useRef({});
  const tableStateRef = useRef({});
  const isInitialMount = useRef(true);

  // ---- Page size state ----
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE_SMALL);

  useEffect(() => {
    get(`${BASE_URL}${API.analistasCredito}/`).then((response) => {
      setAnalistasFilter([...analistasToOptions(response.results), { label: 'No Asignado', value: 'null' }]);
      if (isUserHasProfile([PROFILES.gerenteRiesgoCredito, PROFILES.ceo, PROFILES.administradorTI])) {
        setAnalistasOptions([...analistasToOptions(response.results), { label: 'No Asignado', value: null }])
      }
      else {
        setAnalistasOptions(analistasToOptions(response.results))
      }
    });

    setPendientes(data.count);
  }, [data]);

  useEffect(() => {
    get(`${BASE_URL}${API.paises}?con_deudores=true`).then((response) => {
      const options = response.map((pais) => ({
        label: pais.name,
        value: pais.id,
      }));

      const chileOption = { label: 'Chile', value: 'CL' };
      const hasChile = options.some(option => option.label === 'Chile');

      if (!hasChile) {
        setPaisesOptions([chileOption, ...options]);
      } else {
        setPaisesOptions(options);
      }
    });
  }, []);

  const handleFilterChange = (columnId, value) => {
    if (columnId === 'pais' && value === 'CL') {
      filterValuesRef.current[columnId] = null;
    } else {
      filterValuesRef.current[columnId] = value;
    }
  };

  const adquirirCambios = (movId, analistaId) => {
    const index = arrayAnalistas.map((object) => object.id).indexOf(movId);
    if (index === -1) {
      setArrayAnalistas([...arrayAnalistas, { id: movId, analista: analistaId }]);
    } else {
      const newArr = [...arrayAnalistas];
      newArr[index].analista = analistaId;
      setArrayAnalistas(newArr);
    }
  };

  const eliminarCambios = (disable, movId) => {
    if (!disable) {
      setArrayAnalistas(arrayAnalistas.filter((item) => item.id !== movId));
    }
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const updateAnalista = (objects) => {
    const newArr = objects.map((object) => ({ id: object.id, analista_id: object.analista.value }));
    patch(`${BASE_URL}${API.riskInbox}/asignar-analista/`, newArr).then(() => {
      setArrayAnalistas([]);
      setModalOpen(true);
      getData("");
    });
  };

  const getAnalista = (row) => {
    const index = arrayAnalistas.map((object) => object.id).indexOf(row.original.id);
    if (index !== -1) {
      return arrayAnalistas[index].analista;
    }
    if (row.original.analista) {
      return {
        label: `${row.original.analista.user.first_name} ${row.original.analista.user.last_name}`,
        value: row.original.analista.id,
      };
    }
    return null;
  };

  const isChecked = (row) => {
    const index = arrayAnalistas.map((object) => object.id).indexOf(row.original.id);
    return index !== -1;
  };



  const abrirModalReevaluacion = (row) => {
    setMovimientoIDReevaluacion(row.original.id);
    setUserIDReevaluacion(user.sub);
    setOpenReevaluacionModal(true);
    setNombreAsegurado(row.original.asegurado_nombre);
    setNombreDeudor(row.original.nombre_deudor);
  }

  // ---- Filter apply / clear helpers ----
  const isValidFilterValue = (id, value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') {
      if (value.trim() === '') return false;
      const min = MIN_LENGTHS[id] ?? 1;
      if (value.trim().length < min) return false;
    }
    return true;
  };

  const isEnteredFilterValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    return true;
  };

  const handleApplyFilters = () => {
    const filterValues = filterValuesRef.current;

    const filtered = Object.entries(filterValues)
      .filter(([id, value]) => isValidFilterValue(id, value))
      .map(([id, value]) => ({ id, value }));

    setActiveFilters(Object.entries(filterValues)
      .filter(([_, value]) => isEnteredFilterValue(value))
      .map(([id, value]) => ({ id, value })));

    setFilterCount(filtered.length);

    const newTableState = {
      ...tableStateRef.current,
      filtered,
      page: 0,
      pageSize,
    };

    onFetchData(newTableState);
  };

  const handleClearFilters = () => {
    filterValuesRef.current = {};
    setActiveFilters([]);
    setFilterCount(0);
    setClearSignal(prev => prev + 1);

    const newTableState = {
      ...tableStateRef.current,
      filtered: [],
      page: 0,
      pageSize,
    };

    onFetchData(newTableState);
  };

  const handleFetchData = (tableState) => {
    tableStateRef.current = tableState;

    if (filterCount > 0 && !isInitialMount.current) {
      const validFilters = activeFilters.filter(filter => {
        const value = filter.value || '';
        const min = MIN_LENGTHS[filter.id] ?? 1;
        if (typeof value === 'string' && value.trim().length < min) return false;
        return true;
      });

      const newTableState = {
        ...tableState,
        filtered: validFilters,
        pageSize: tableState.pageSize || pageSize,
      };

      isInitialMount.current = false;
      return onFetchData(newTableState);
    }

    isInitialMount.current = false;
    return onFetchData(tableState);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);

    const validFilters = filterCount > 0 
      ? activeFilters.filter(filter => {
          const value = filter.value || '';
          const min = MIN_LENGTHS[filter.id] ?? 1;
          if (typeof value === 'string' && value.trim().length < min) return false;
          return true;
        })
      : [];

    const newTableState = {
      ...tableStateRef.current,
      filtered: validFilters,
      page: 0,
      pageSize: size,
    };

    onFetchData(newTableState);
  };

  const createFilterInput = (filterKey, placeholder, minLength = 1) => () => (
    <FilterInput
      columnId={filterKey}
      placeholder={placeholder}
      onChange={handleFilterChange}
      minLength={minLength}
      clearSignal={clearSignal}
      initialValue={filterValuesRef.current[filterKey] || ''}
    />
  );

  const columns = [
    {
      Header: headerOperation("ID", "left"),
      id: "id__icontains",
      accessor: "id",
      Cell: (row) => cellOperation(row.value),
      minWidth: 70,
      Filter: createFilterInput('id__icontains', 'Filtrar ID'),
    },
    {
      Header: headerDoubleRowOperation("Tipo de", "solicitud"),
      id: "tipo__descripcion__icontains",
      accessor: "tipo_descripcion",
      Cell: (row) => cellOperation(row.value, "left"),
      minWidth: 110,
      Filter: createFilterInput('tipo__descripcion__icontains', 'Mínimo 3 caracteres', 3),
    },
    {
      Header: headerOperation("Asegurado", "left"),
      id: "linea_credito__cliente",
      accessor: "asegurado_nombre",
      Cell: (row) => cellOperation(row.value, "left"),
      minWidth: 190,
      Filter: createFilterInput('linea_credito__cliente', 'Mínimo 3 caracteres', 3),
    },
    {
      Header: headerOperation("Póliza", "left"),
      id: "poliza",
      accessor: "poliza",
      Cell: (row) => cellOperation(row.value),
      minWidth: 120,
      Filter: createFilterInput('poliza', 'Filtrar póliza'),
    },
    {
      Header: headerOperation("Nombre deudor", "left"),
      id: "razon",
      accessor: "nombre_deudor",
      Cell: (row) =>
        cellOperation(
          <Link to={`/posicion-deudor/${row.original.id_deudor}`} className="table-link">
            {row.value}
          </Link>,
          "left",
        ),
      minWidth: 190,
      Filter: createFilterInput('razon', 'Mínimo 3 caracteres', 3),
    },
    {
      Header: headerDoubleRowOperation("N° documento", "deudor"),
      id: "rut",
      accessor: "rut_deudor",
      Cell: (row) => {
        const countryCode = row.original.pais_deudor_code ? row.original.pais_deudor_code : 'CL';
        return cellOperation(countryCode === 'CL' ? formatRut(row.value) : row.value, 'left');
      },
      minWidth: 150,
      Filter: createFilterInput('rut', 'Mínimo 3 caracteres', 3),
    },
    {
      Header: headerOperation("País deudor", "left"),
      id: "pais",
      accessor: "pais_deudor_name",
      Cell: (row) => cellOperation(row.value, "left"),
      Filter: () => (
        <FilterSelectWrapper
          columnId="pais"
          options={paisesOptions}
          defaultOption={{ label: 'Todos', value: NO_FILTERED_ID }}
          onChange={handleFilterChange}
          clearSignal={clearSignal}
          initialValue={filterValuesRef.current.pais ?? null}
        />
      ),
      minWidth: 130,
    },
    {
      Header: headerOperation("Tipo Solicitante", "left"),
      id: "tipo_solicitante",
      accessor: "solicitante.organization.is_main",
      Cell: (row) => cellOperation(row.value == null && '-' || (row.value ? 'Interno' : 'Externo')),
      minWidth: 150,
      filterable: false,
      sortable: false,
    },
    {
      Header: headerOperation("Solicitante", "left"),
      id: "solicitante",
      accessor: "solicitante",
      Cell: (row) => cellOperation(row.value ? `${row.value.user.first_name} ${row.value.user.last_name}` : '-'),
      minWidth: 150,
      filterable: false,
      sortable: false,
    },
    {
      Header: headerDoubleRowOperation("Moneda", "póliza"),
      id: "moneda_poliza",
      accessor: "moneda_poliza",
      Cell: (row) => cellOperation(row.value),
      minWidth: 90,
      filterable: false,
      sortable: false,
    },
    {
      Header: headerDoubleRowOperation("Techo", "vigente"),
      id: "monto_maximo_deudor",
      accessor: "monto_maximo_deudor",
      Cell: (row) => cellNumOperation(row.value, "right"),
      minWidth: 100,
      filterable: false,
    },
    {
      Header: headerDoubleRowOperation("Cúmulo", "vigente"),
      id: "cumulo_vigente",
      accessor: "cumulo_vigente",
      Cell: (row) => cellNumOperation(row.value, "right"),
      minWidth: 100,
      filterable: false,
    },
    {
      Header: headerDoubleRowOperation("Línea", "solicitada"),
      id: "monto_solicitado__icontains",
      accessor: "linea_solicitada",
      Cell: (row) => cellNumOperation(row.value, "right"),
      minWidth: 120,
      Filter: createFilterInput('monto_solicitado__icontains', 'Filtrar línea'),
    },
    {
      Header: headerOperation("Clasificación", "left"),
      id: "nominado",
      accessor: "nominado",
      Cell: (row) => cellOperation(row.value, "left"),
      minWidth: 130,
      Filter: createFilterInput('nominado', 'Mínimo 3 caracteres', 3),
    },
    {
      Header: headerDoubleRowOperation("Fecha", "solicitud"),
      id: "fecha_solicitud",
      accessor: "fecha_solicitud",
      Cell: (row) => cellOperation(fdate(row.value, FRONT_DF_1), "left"),
      minWidth: 100,
      filterable: false,
    },
    {
      Header: headerDoubleRowOperation("Tiempo", "en espera"),
      id: "tiempo_espera",
      accessor: "tiempo_espera",
      // eslint-disable-next-line react/prop-types
      Cell: ({ value }) => {
        const hours = parseTiempoEspera(value);
        const color = getUrgencyColor(hours);
        return <span style={{ color }}>{value || "---"}</span>;
      },
      minWidth: 100,
      filterable: false,
    },
    {
      Header: headerDoubleRowOperation('Decisión', 'motor'),
      id: 'decision_bypass_monto',
      accessor: 'decision_bypass_monto',
      Cell: (row) =>
        cellOperation(
          <div>
            <Icon
              id={`decision_bypass_monto-icon-${row.original.id}`}
              tooltipText={`Decisión motor: ${row.original.razones_motor_riesgo.length ? 'Rechazado' : 'Aprobado'}` || '---'}
            >
              <FontAwesomeIcon icon={faExclamationCircle} style={{ color: 'var(--orsan-dark-grey)' }} />
            </Icon>
            {(row.original.razones_motor_riesgo.length > 0 && (
              <Icon
                id={`razones_motor_riesgo-icon-${row.original.id}`}
                autohide={false}
                tooltipText={(
                  <>
                    <div>Razones:</div>
                    {(row.original.razones_motor_riesgo.map((razon, index) => (
                      <li key={`${index + 1}`} style={{ justifyContent: 'center' }}>{razon}</li>
                    )))}
                  </>
                )}
              >
                <FontAwesomeIcon icon={faExclamationCircle} style={{ color: 'var(--orsan-dark-grey)' }} />
              </Icon>
            ))}
          </div>,
        ),
      minWidth: 140,
      filterable: false,
      sortable: false,
    },
    {
      Header: headerDoubleRowOperation("Solicitar", "Reevaluación"),
      id: "reevaluacion",
      Cell: (row) => cellOperation(
        <Button onClick={() => abrirModalReevaluacion(row)} className="table-link download">
          <FontAwesomeIcon icon={faRedo} />
        </Button>,
        "center",
      ),
      sortable: false,
      filterable: false,
      minWidth: 120,
    },
    {
      Header: headerDoubleRowOperation("Observación", "asegurado"),
      id: "observacion_asegurado",
      accessor: "observacion_asegurado",
      Cell: (row) =>
        cellOperation(
          <Icon id={`obs-${row.original.id}`} tooltipText={row.value}>
            <FontAwesomeIcon icon={faInfoCircle} />
          </Icon>,
          "center",
        ),
      minWidth: 120,
      sortable: false,
      filterable: false,
    },
    {
      Header: headerOperation("Analista", "left"),
      id: "analista_id",
      // fixed: "right",
      accessor: "analista",
      // eslint-disable-next-line
      Filter: () => (
        <>
          <FilterSelectWrapper
            columnId="analista_id"
            options={analistasFilter}
            defaultOption={{ label: 'Todos', value: NO_FILTERED_ID }}
            onChange={handleFilterChange}
            clearSignal={clearSignal}
            initialValue={filterValuesRef.current.analista_id ?? null}
          />
          <Button
            block
            className="btn-orsan"
            style={{ padding: 0 }}
            onClick={() => updateAnalista(arrayAnalistas)}
            // arrayAnalistas.length
            disabled={!isUserHasProfile(INBOX_VIEW_WRITE) || (arrayAnalistas.length === 0)}
          >
            Asignar
          </Button>
        </>
      ),
      Cell: (i) => (
        <CheckboxDropdown
          analista={getAnalista(i)}
          analistas={analistasOptions}
          agregar={adquirirCambios}
          quitar={eliminarCambios}
          movId={i.original.id}
          checked={isChecked(i)}
        />
      ),
      minWidth: 220,
      sortable: false,
    },
    {
      Header: headerOperation("Resolver", "left"),
      id: "resolver",
      // fixed: "right",
      sortable: false,
      accessor: "id",
      Cell: (row) => (
        cellOperation(
          isUserHasProfile(INBOX_VIEW_WRITE) ? (
            <Link to={`/inbox-riesgos/${row.value}`} className="table-link">
              <FontAwesomeIcon icon={faEdit} />
            </Link>
          ) : <></>,
        )
      ),
      minWidth: 90,
      filterable: false,
    },
  ];

  return (
    <div style={{ margin: "25px 0 40px 0" }} className='table-bandeja-entrada'>
      <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          onClick={handleApplyFilters}
          style={{
            padding: '8px 16px', backgroundColor: '#007bff', color: 'white',
            border: 'none', borderRadius: '4px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '6px'
          }}
        >
          <FontAwesomeIcon icon={faFilter} />
          Aplicar Filtro
        </Button>

        <Button
          onClick={handleClearFilters}
          style={{
            padding: '8px 16px',
            backgroundColor: activeFilters.length > 0 ? '#dc3545' : '#6c757d',
            color: 'white', border: 'none', borderRadius: '4px',
            cursor: activeFilters.length > 0 ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', gap: '6px'
          }}
          disabled={activeFilters.length === 0}
        >
          <FontAwesomeIcon icon={faTimes} />
          Limpiar Filtros
        </Button>

        {activeFilters.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              Filtros ingresados: {activeFilters.length}
            </span>
            {filterCount > 0 && filterCount !== activeFilters.length && (
              <span style={{ color: '#007bff', fontSize: '14px', fontWeight: 'bold' }}>
                Filtros aplicados: {filterCount}
              </span>
            )}
            {filterCount === 0 && activeFilters.length > 0 && (
              <span style={{
                color: '#dc3545', fontSize: '14px', backgroundColor: '#fff5f5',
                padding: '4px 8px', borderRadius: '4px', border: '1px solid #ffcdd2'
              }}
              >
                ⚠️ Los filtros requieren el mínimo de caracteres indicado
              </span>
            )}
          </div>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#666', fontSize: '14px' }}>Mostrar:</span>
          {PAGE_SIZE_OPTIONS.map((size) => (
            <Button
              key={size}
              onClick={() => handlePageSizeChange(size)}
              style={{
                padding: '6px 12px',
                backgroundColor: pageSize === size ? '#007bff' : '#e9ecef',
                color: pageSize === size ? 'white' : '#333',
                border: 'none', borderRadius: '4px', cursor: 'pointer',
              }}
            >
              {size}
            </Button>
          ))}
        </div>
      </div>

      <BaseTable
        columns={columns}
        data={data.data}
        loading={loading}
        onFetchData={handleFetchData}
        pages={Math.ceil(data.count / pageSize)}
        pageSize={pageSize}
        resultCount={data.count}
        hasFixedColumns
        showPagination
        filterable
        sortable
        style={{ maxHeight: "63vh" }}
      />
      <CheckModal
        isOpen={modalOpen}
        toggle={toggleModal}
        handleOk={toggleModal}
        okBtnText="OK"
        confirmMsg="Los cambios se han realizado correctamente."
      />
      <SolicitudReevaluacionModal
        openReevaluacionModal={openReevaluacionModal}
        setOpenReevaluacionModal={setOpenReevaluacionModal}
        movimientoIDReevaluacion={movimientoIDReevaluacion}
        userIDReevaluacion={userIDReevaluacion}
        nombreAsegurado={nombreAsegurado}
        nombreDeudor={nombreDeudor}
      />
    </div>
  );
};

InboxRiesgoMovimientosTable.propTypes = {
  setPendientes: PropTypes.func.isRequired,
}

export default InboxRiesgoMovimientosTable;
